import { httpsCallable } from 'firebase/functions';
import type {
  BillingSetPaymentMethodRequest,
  BillingSetPaymentMethodResponse,
  BillingCreateBankTransferChargeRequest,
  BillingCreateBankTransferChargeResponse,
  BillingUpdateBankTransferChargeRequest,
  BillingUpdateBankTransferChargeResponse,
} from '@kawai/shared';
import { functions } from '../firebase.js';

// TP-27: 銀行振込請求管理の Callable 3 本の薄いラッパ + 日本語エラー変換。
// いずれも saas-admin 限定（サーバ側 assertSaasAdminCaller + assertWritableCaller）。

const billingSetPaymentMethodCallable = httpsCallable<
  BillingSetPaymentMethodRequest,
  BillingSetPaymentMethodResponse
>(functions, 'billingSetPaymentMethod');

const billingCreateBankTransferChargeCallable = httpsCallable<
  BillingCreateBankTransferChargeRequest,
  BillingCreateBankTransferChargeResponse
>(functions, 'billingCreateBankTransferCharge');

const billingUpdateBankTransferChargeCallable = httpsCallable<
  BillingUpdateBankTransferChargeRequest,
  BillingUpdateBankTransferChargeResponse
>(functions, 'billingUpdateBankTransferCharge');

/** TP-27: `billingSetPaymentMethod`（tenants.paymentMethod 切替）の薄いラッパ。 */
export async function callBillingSetPaymentMethod(
  input: BillingSetPaymentMethodRequest,
): Promise<BillingSetPaymentMethodResponse> {
  const res = await billingSetPaymentMethodCallable(input);
  return res.data;
}

/** TP-27: `billingCreateBankTransferCharge`（振込請求の発行）の薄いラッパ。 */
export async function callBillingCreateBankTransferCharge(
  input: BillingCreateBankTransferChargeRequest,
): Promise<BillingCreateBankTransferChargeResponse> {
  const res = await billingCreateBankTransferChargeCallable(input);
  return res.data;
}

/** TP-27: `billingUpdateBankTransferCharge`（消し込み / 取消）の薄いラッパ。 */
export async function callBillingUpdateBankTransferCharge(
  input: BillingUpdateBankTransferChargeRequest,
): Promise<BillingUpdateBankTransferChargeResponse> {
  const res = await billingUpdateBankTransferChargeCallable(input);
  return res.data;
}

/**
 * Firebase Functions の Callable エラー（FunctionsError）相当かを duck typing で判定する
 * （`code` が `functions/<reason>` 形を満たすか / billingCancelSubscription.ts と同方針）。
 */
interface FunctionsErrorLike {
  code: string;
  message?: string;
}

function isFunctionsErrorLike(err: unknown): err is FunctionsErrorLike {
  return (
    typeof err === 'object' &&
    err !== null &&
    'code' in err &&
    typeof (err as { code: unknown }).code === 'string' &&
    (err as { code: string }).code.startsWith('functions/')
  );
}

/**
 * TP-27 の 3 Callable 共通の日本語エラー変換。`opLabel` は操作名
 * （"支払方法の変更" / "請求の発行" / "請求の更新"）で、失敗文言の主語になる。
 * already-exists / failed-precondition はサーバ側が操作別の具体文言を返すためそれを優先する。
 */
export function bankTransferBillingErrorMessage(
  opLabel: string,
  err: unknown,
): string {
  if (isFunctionsErrorLike(err)) {
    switch (err.code) {
      case 'functions/already-exists':
        return err.message ?? `${opLabel}は既に処理済みです。`;
      case 'functions/failed-precondition':
        return err.message ?? `${opLabel}の前提条件を満たしていません。`;
      case 'functions/not-found':
        return err.message ?? '対象が見つかりません。';
      case 'functions/invalid-argument':
        return `入力値に誤りがあります：${err.message ?? ''}`;
      case 'functions/permission-denied':
        return '権限がありません（saas-admin 権限が必要です）。';
      case 'functions/unauthenticated':
        return 'セッションが切れました。再ログインしてください。';
      default:
        return `${opLabel}に失敗しました（${err.code}）。`;
    }
  }
  if (err instanceof Error) {
    return `${opLabel}に失敗しました：${err.message}`;
  }
  return `${opLabel}に失敗しました。`;
}
